import { titleize } from "@/helpers/filters";
import client from "@/api/openapi-ts";
import { fetchExportDownload } from "@/lib/exports/downloadFile";
import type { components } from "@/api/schema.d.ts";
import {
  MetaAudiencesRequest,
  MetaAudience,
  MetaAudienceChangeHistory,
  MetaAudienceSummary,
} from "@/types/api/MetaAudience";
import { defineStore, acceptHMRUpdate } from "pinia";
import { computed, ref } from "vue";
import { PaginatedData, PaginationArgs } from "@/types/api/common";
import { PartnerCut } from "@/types/api/PartnerCut";
import { Partner } from "@/types/api/Partner";
import { Audience } from "@/types/api/Audience";
import { UserActivity } from "@/types/api/UserActivity";
import { useExports } from "./helpers/useExports";
import { usePlugins } from "./helpers/usePlugins";
import { STANDARD_META_AUDIENCE_FIELDS } from "@/helpers/metaAudience";

type schemas = components["schemas"];

type HighLows = {
  high_streaming?: number;
  low_streaming?: number;
  high_linear_TV?: number;
  low_linear_TV?: number;
  "low_linear_/_high_streaming"?: number;
  "high_linear_/_high_streaming"?: number;
  "low_linear_/_low_streaming"?: number;
  "high_linear_/_low_streaming"?: number;
};

type PendingTaskResponse = {
  task_id: string;
};

type SummaryCounts = {
  loading: boolean;
  task_id: string | undefined;
  count: number | undefined;
};

export const useMetaAudienceStore = defineStore("metaAudience", () => {
  const { $ws } = usePlugins();
  const loading = ref(false);
  const loadingMetaAudiences = ref(false);
  const loadingAll = ref(false);
  const loadingInternal = ref(false);
  const loadingEventHistory = ref(false);
  const saving = ref(false);
  const error = ref("");
  const metaAudiences = ref<PaginatedData<MetaAudience>>({});
  const metaAudiencesForPartner = ref<PaginatedData<MetaAudience>>({});
  const metaAudiencesSummary = ref<PaginatedData<MetaAudienceSummary>>({});
  const eventHistory = ref<UserActivity[]>([]);
  const metaAudienceChangeHistory = ref<MetaAudienceChangeHistory[]>([]);
  const unpaginatedMetaAudiences = ref<MetaAudience[]>([]);
  const internalMetaAudiences = ref<PaginatedData<MetaAudience>>({});
  const selectedMetaAudience = ref<{
    audience?: MetaAudience;
    partners?: PartnerCut[];
  }>({});
  const selectedMetaAudienceByName = ref<MetaAudience>({});
  const premiumAudiences = ref<MetaAudience[]>([]);
  const availabilities = ref<{ name: string; id: number }[]>([]);
  const previewCount = ref<number | undefined>(undefined);
  const loadingTvCategories = ref(false);
  const tvCategories = ref<MetaAudience[]>([]);
  const metaAudiencePreview = ref<Audience>({});
  const userCreatedMetaAudiences = ref<MetaAudience[]>([]);
  const brandMetaAudiences = ref<MetaAudience[]>([]);
  const highLowIds = ref<HighLows>({});
  const audienceBuilderStartTime = ref(0);
  const audienceCreators = ref<schemas["MetaAudienceCreatedByUserResponse"][]>(
    [],
  );
  const summaryCounts = ref<{ [key: string]: SummaryCounts }>({});
  // Shared export workflow: owns the export's loading/task state, websocket
  // subscription, and the file download once the task completes.
  const exportManager = useExports<"csv" | "pre_built" | "marketplace">({
    channel: "MetaAudience",
    keys: ["csv", "pre_built", "marketplace"],
    fetchFile: (file, signal) =>
      fetchExportDownload(
        "/api/v1/meta_audiences/export/{filename}",
        file,
        signal,
      ),
  });
  const loadingExport = computed(
    () =>
      exportManager.loading.csv ||
      exportManager.loading.pre_built ||
      exportManager.loading.marketplace,
  );

  return {
    // getters
    loading,
    loadingMetaAudiences,
    loadingAll,
    loadingInternal,
    loadingEventHistory,
    saving,
    error,
    selectedMetaAudience,
    selectedMetaAudienceByName,
    metaAudiences,
    userCreatedMetaAudiences: computed(() => {
      return [...userCreatedMetaAudiences.value].sort((a, b) => {
        return a.name!.localeCompare(b.name!);
      });
    }),
    brandMetaAudiences: computed(() => {
      return [...brandMetaAudiences.value].sort((a, b) => {
        return a.name!.localeCompare(b.name!);
      });
    }),
    metaAudiencesSummary,
    eventHistory,
    metaAudiencesForPartner,
    metaAudienceChangeHistory,
    unpaginatedMetaAudiences,
    internalMetaAudiences,
    metaAudiencesSorted: computed(() => {
      if (!metaAudiences.value.data) return [];
      return [...metaAudiences.value.data].sort((a, b) => {
        return a.name!.localeCompare(b.name!);
      });
    }),
    accessibleAudienceIssues: computed(() => {
      if (!metaAudiences.value.data) return [];

      return metaAudiences.value.data
        .map((a) => a.audience_issues)
        .flat()
        .filter(
          (issue, idx, self) =>
            idx ===
            self.findIndex(
              (item) => item?.id === issue?.id && item?.name === issue?.name,
            ),
        )
        .sort((a, b) => a!.name.localeCompare(b!.name));
    }),
    premiumAudiences,
    haloPremiumAudiences: computed(() => {
      return premiumAudiences.value
        .filter(
          (a) =>
            a.audience_type?.name === "Premium" &&
            a.audience_category?.name === "Halo",
        )
        .sort((a, b) => {
          return a.name!.localeCompare(b.name!);
        })
        .map((a) => ({
          id: a.id,
          name: a.name,
        }));
    }),
    availabilities,
    tvCategories,
    loadingTvCategories,
    previewCount,
    metaAudiencePreview,
    highLowIds,
    audienceBuilderStartTime,
    audienceCreators,
    summaryCounts,
    loadingExport,
    exportErrors: exportManager.errors,
    handleExportMessage: exportManager.handleMessage,
    // actions
    getAvailabilities: async () => {
      loading.value = true;
      try {
        const { data } = await client.GET(
          "/api/v1/meta_audiences/availabilities",
        );
        availabilities.value = Object.entries(
          (data as Record<string, number> | undefined) ?? {},
        ).map(([k, v]) => ({ id: v, name: k }));
      } finally {
        loading.value = false;
      }
    },
    getPremiumAudiences: async () => {
      loading.value = true;
      try {
        const { data } = await client.GET("/api/v1/meta_audiences/premium");
        premiumAudiences.value =
          (data as unknown as MetaAudience[] | undefined) ?? [];
      } finally {
        loading.value = false;
      }
    },
    get: async (id: number, fields?: string[]) => {
      const defaultFields = [
        "audience_issues",
        "id",
        "long_description",
        "name",
        "refreshed",
        "states",
        "uses_fec_donor_data",
      ];
      const { data, error } = await client.GET(
        "/api/v1/meta_audiences/{meta_audience_id}",
        {
          params: {
            path: { meta_audience_id: id },
            query: { fields: fields ?? defaultFields } as never,
          },
        },
      );
      if (error) throw error;
      return data as unknown as MetaAudience;
    },
    getMetaAudience: async (args: { id: number; fields?: string[] }) => {
      loading.value = true;
      try {
        const [audienceResponse, partnerCutsResponse] = await Promise.all([
          client.GET("/api/v1/meta_audiences/{meta_audience_id}", {
            params: {
              path: { meta_audience_id: args.id },
              query: { fields: args.fields } as never,
            },
          }),
          client.GET("/api/v1/meta_audiences/{meta_audience_id}/partner_cuts", {
            params: { path: { meta_audience_id: args.id } },
          }),
        ]);
        selectedMetaAudience.value = {
          audience: audienceResponse.data as unknown as MetaAudience,
          partners: partnerCutsResponse.data as unknown as PartnerCut[],
        };
        return selectedMetaAudience.value;
      } catch {
        error.value = "Failed to find audience";
      } finally {
        loading.value = false;
      }
    },
    getMetaAudienceHistory: async (audienceId: number) => {
      loading.value = true;
      try {
        const { data } = await client.GET(
          "/api/v1/meta_audiences/{meta_audience_id}/change_history",
          {
            params: { path: { meta_audience_id: audienceId } },
          },
        );
        const history = (
          (data as unknown as MetaAudienceChangeHistory[] | undefined) ?? []
        ).map((h) => {
          if (h.new_value instanceof Array) {
            h.new_value = h.new_value.join();
          }
          if (h.previous_value instanceof Array) {
            h.previous_value = h.previous_value.join();
          }
          return h;
        });
        metaAudienceChangeHistory.value = history;
      } finally {
        loading.value = false;
      }
    },
    getMetaAudiences: async (params: MetaAudiencesRequest = {}) => {
      loading.value = true;
      loadingMetaAudiences.value = true;
      const requestParams: MetaAudiencesRequest = {
        ...params,
        fields: params.fields ?? [...STANDARD_META_AUDIENCE_FIELDS],
      };
      try {
        const { data, error, response } = await client.GET(
          "/api/v1/meta_audiences",
          {
            params: { query: requestParams as never },
          },
        );
        if (error) {
          throw error;
        }
        metaAudiences.value = {
          pagination: {
            page: Number(response.headers.get("current-page")),
            page_items: Number(response.headers.get("page-items")),
            total_pages: Number(response.headers.get("total-pages")),
            total_items: Number(response.headers.get("total-count")),
          },
          data: data as unknown as MetaAudience[],
          params: requestParams,
        };
      } finally {
        loading.value = false;
        loadingMetaAudiences.value = false;
      }
    },
    getInternalMetaAudiences: async (params: MetaAudiencesRequest) => {
      loadingInternal.value = true;
      try {
        const { data, error, response } = await client.GET(
          "/api/v1/meta_audiences",
          {
            params: { query: params as never },
          },
        );
        if (error) {
          throw error;
        }
        internalMetaAudiences.value = {
          pagination: {
            page: Number(response.headers.get("current-page")),
            page_items: Number(response.headers.get("page-items")),
            total_pages: Number(response.headers.get("total-pages")),
            total_items: Number(response.headers.get("total-count")),
          },
          data: data as unknown as MetaAudience[],
          params,
        };
        unpaginatedMetaAudiences.value = [];
      } finally {
        loadingInternal.value = false;
      }
    },
    getUserCreatedMetaAudiences: async (userCreatedTypeId: number) => {
      loading.value = true;
      try {
        const { data, error } = await client.GET("/api/v1/meta_audiences", {
          params: {
            query: {
              audience_type_ids: [userCreatedTypeId],
              fields: ["id", "name"],
            } as never,
          },
        });
        if (error) {
          throw error;
        }
        userCreatedMetaAudiences.value =
          (data as unknown as MetaAudience[]) ?? [];
      } finally {
        loading.value = false;
      }
    },
    getMetaAudiencesForBrand: async (brandId: number) => {
      loading.value = true;
      try {
        const { data, error } = await client.GET("/api/v1/meta_audiences", {
          params: {
            query: {
              audience_brand_ids: [brandId],
              fields: ["id", "name"],
            } as never,
          },
        });
        if (error) {
          throw error;
        }
        brandMetaAudiences.value = (data as unknown as MetaAudience[]) ?? [];
      } finally {
        loading.value = false;
      }
    },
    getMetaAudiencesSummary: async (args: PaginationArgs) => {
      loading.value = true;
      try {
        const { data, error, response } = await client.GET(
          "/api/v1/meta_audiences/summary",
          {
            params: { query: args as never },
          },
        );
        if (error) {
          throw error;
        }
        metaAudiencesSummary.value = {
          pagination: {
            page: Number(response.headers.get("current-page")),
            page_items: Number(response.headers.get("page-items")),
            total_pages: Number(response.headers.get("total-pages")),
            total_items: Number(response.headers.get("total-count")),
          },
          data: data as unknown as MetaAudienceSummary[],
          args,
        };
      } finally {
        loading.value = false;
      }
    },
    getEventHistory: async (audience_id: number) => {
      eventHistory.value = [];
      loadingEventHistory.value = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/meta_audiences/{meta_audience_id}/event_history",
          {
            params: { path: { meta_audience_id: audience_id } },
          },
        );
        if (error) {
          throw error;
        }
        eventHistory.value = (data as unknown as UserActivity[]) ?? [];
      } finally {
        loadingEventHistory.value = false;
      }
    },
    getMetaAudienceByName: async (audience_name: string) => {
      loading.value = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/meta_audiences/find",
          {
            params: { query: { search: audience_name } as never },
          },
        );
        if (error) {
          throw error;
        }
        selectedMetaAudienceByName.value = data as unknown as MetaAudience;
      } finally {
        loading.value = false;
      }
    },
    getHighLowAudiences: async (audience_name: string & keyof HighLows) => {
      loading.value = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/meta_audiences/find",
          {
            params: { query: { search: titleize(audience_name) } as never },
          },
        );
        if (error) {
          throw error;
        }
        highLowIds.value[audience_name] = (data as unknown as MetaAudience).id!;
      } finally {
        loading.value = false;
      }
    },
    searchMetaAudiences: async (
      search: string,
      myAudiencesOnly: boolean = false,
    ) => {
      try {
        const { data, error } = await client.GET(
          "/api/v1/meta_audiences/search",
          {
            params: {
              query: { q: search, mine_only: myAudiencesOnly } as never,
            },
          },
        );
        if (error) {
          throw error;
        }
        return data as unknown as MetaAudience[];
      } catch (error) {
        console.error("searchMetaAudiences", error);
      }
      return [];
    },
    getUnpaginatedMetaAudiences: async () => {
      loadingAll.value = true;
      try {
        const { data, error } = await client.GET("/api/v1/meta_audiences", {
          params: {
            query: {
              ...internalMetaAudiences.value.params,
              per_page: undefined,
              page: undefined,
            } as never,
          },
        });
        if (error) {
          throw error;
        }
        unpaginatedMetaAudiences.value =
          (data as unknown as MetaAudience[]) ?? [];
      } finally {
        loadingAll.value = false;
      }
    },
    getMetaAudiencesForPartner: async (
      partner: Partner,
      params: MetaAudiencesRequest,
    ) => {
      loading.value = true;
      try {
        const { data, error, response } = await client.GET(
          "/api/v1/meta_audiences",
          {
            params: { query: { partner_id: partner.id, ...params } as never },
          },
        );
        if (error) {
          throw error;
        }
        const sortedAudiences = (
          (data as unknown as MetaAudience[]) ?? []
        ).sort((a, b) => {
          return a.name!.localeCompare(b.name!);
        });
        metaAudiencesForPartner.value = {
          pagination: {
            page: Number(response.headers.get("current-page")),
            page_items: Number(response.headers.get("page-items")),
            total_pages: Number(response.headers.get("total-pages")),
            total_items: Number(response.headers.get("total-count")),
          },
          data: sortedAudiences,
        };
      } finally {
        loading.value = false;
      }
    },
    addMetaAudience: (metaAudience: MetaAudience) => {
      if (metaAudiences.value.data) {
        metaAudiences.value.data.push(metaAudience);
      }
    },
    updateMetaAudience: async (metaAudience: MetaAudience) => {
      saving.value = true;
      try {
        const { data } = await client.PUT(
          "/api/v1/meta_audiences/{meta_audience_id}",
          {
            params: { path: { meta_audience_id: metaAudience.id! } },
            body: metaAudience as never,
          },
        );
        const updated = data as unknown as MetaAudience;
        const existing = metaAudiences.value.data?.find(
          (ma) => ma.id === updated.id,
        );
        if (existing) {
          Object.assign(existing, updated);
        }
        const internalMetaAudience = internalMetaAudiences.value.data?.find(
          (ma) => ma.id === updated.id,
        );
        if (internalMetaAudience) {
          Object.assign(internalMetaAudience, updated);
        }
        const metaAudienceSummary = metaAudiencesSummary.value.data?.find(
          (ma) => ma.id === updated.id,
        );
        if (metaAudienceSummary) {
          metaAudienceSummary.shared_with = updated.shared_with || [];
        }
      } finally {
        saving.value = false;
      }
    },
    updateMetaAudienceActivate: (metaAudienceId: number) => {
      return client.POST("/api/v1/meta_audiences/activate", {
        body: { meta_audience_id: metaAudienceId } as never,
      });
    },
    bulkUpdateMetaAudiences: async (args: {
      meta_audience_ids: number[];
      meta_audience_params: Partial<MetaAudience>;
    }) => {
      saving.value = true;
      try {
        const { data } = await client.PUT(
          "/api/v1/meta_audiences/bulk_update",
          {
            body: args as never,
          },
        );
        ((data as unknown as MetaAudience[]) ?? []).forEach((ma) => {
          const metaAudience = metaAudiences.value.data?.find(
            (a) => a.id === ma.id,
          );
          if (metaAudience) {
            Object.assign(metaAudience, ma);
          }
          const internalMetaAudience = internalMetaAudiences.value.data?.find(
            (a) => a.id === ma.id,
          );
          if (internalMetaAudience) {
            Object.assign(internalMetaAudience, ma);
          }
        });
      } finally {
        saving.value = false;
      }
    },
    deleteMetaAudience: async (id: number) => {
      saving.value = true;
      try {
        return await client.DELETE(
          "/api/v1/meta_audiences/{meta_audience_id}/delete",
          {
            params: { path: { meta_audience_id: id } },
          },
        );
      } finally {
        saving.value = false;
      }
    },
    getTvCategories: async () => {
      loadingTvCategories.value = true;
      try {
        const { data } = await client.GET(
          "/api/v1/meta_audiences/tv_categories",
        );
        tvCategories.value = (data as unknown as MetaAudience[]) ?? [];
      } finally {
        loadingTvCategories.value = false;
      }
    },
    getAudienceCreators: async () => {
      try {
        const { data } = await client.GET(
          "/api/v1/meta_audiences/created_by_users",
        );
        audienceCreators.value = data ?? [];
      } catch (error) {
        console.error("getAudienceCreators", error);
      }
    },
    create_export(
      exportType: "pre_built" | "marketplace" = "pre_built",
      categoryIds?: number[],
    ) {
      return exportManager.startExport(exportType, () =>
        client.POST("/api/v1/meta_audiences/export", {
          body: {
            export_type: exportType,
            category_ids: categoryIds?.length ? categoryIds : undefined,
          } as schemas["MetaAudienceExportRequest"],
        }),
      );
    },
    create_catalog_export(
      exportType: "csv" | "pre_built" | "marketplace",
      categoryIds?: number[],
      includeMyAudiences = false,
    ) {
      return exportManager.startExport(exportType, () =>
        client.POST("/api/v1/meta_audiences/catalog/export", {
          body: {
            export_type: exportType,
            category_ids: categoryIds?.length ? categoryIds : undefined,
            include_my_audiences: includeMyAudiences,
          } as schemas["MetaAudienceCatalogExportRequest"],
        }),
      );
    },
    getSummaryCounts: async (audienceIds: number[]) => {
      const fetchCount = async (audienceId: number) => {
        summaryCounts.value[audienceId] = {
          loading: true,
          task_id: undefined,
          count: undefined,
        };
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/counts",
            {
              params: {
                path: {
                  meta_audience_id: audienceId,
                  geography_id: 1, // national count
                },
              },
            },
          );
          const taskId = (data as PendingTaskResponse | undefined)?.task_id;
          if (taskId) {
            summaryCounts.value[audienceId].task_id = taskId;
            $ws?.subscribe({
              channel: "MetaAudience",
              payload: { id: taskId },
            });
          } else {
            summaryCounts.value[audienceId].loading = false;
          }
        } catch (error) {
          console.error("getSummaryCounts", audienceId, error);
          summaryCounts.value[audienceId].loading = false;
        }
      };

      await Promise.allSettled(audienceIds.map(fetchCount));
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useMetaAudienceStore, import.meta.hot),
  );
}
